import { cn } from "@/lib/utils";

// The shared stat-tile row (Story 15.7, D5).
//
// Before this file there was no stat/tile primitive anywhere in the design
// system: `StatsGrid` was declared TWICE, locally and non-exported, in
// `routes/org-admin/OrgAdminDashboardScreen.tsx` and
// `routes/org-admin/SeatsAllocationScreen.tsx` — byte-identical in markup and
// differing only in their fourth tile. Screen 10 needs six tiles and Screen 23
// needs four, so those would have been the third and fourth copies.
//
// It takes the FINISHED array rather than a payload. Both org-admin copies read
// `SeatPoolData` directly, which is exactly what could not be shared: Screen
// 10's tiles come from `GET /admin/console`; Screen 23's from
// `GET /admin/orgs/totals` (`useAdminOrgTotals`), which DW-423 carved out of the
// console read. A prop typed to one screen's payload is what kept the two copies
// apart in the first place.

/** One tile — a label above a number. Every call site builds its own array. */
export interface StatTile {
  label: string;
  /**
   * A string is admitted for the one honest non-number: a figure the server did
   * not send. `active_seat_count` is optional on `AdminOrgRow` (added by Story
   * 21.4), and a tile that printed `0` for "not reported" would state a
   * headcount rather than decline to.
   */
  value: number | string;
  /**
   * Optional line under the figure — Story 22.1's Seat-impact block uses it for
   * the movement this action causes (`+1 — was 118`), which is the whole point
   * of showing post-action counts: a number with no before is not an impact.
   */
  foot?: string;
}

/**
 * How many tiles share a row at `md` and above. Below that breakpoint every
 * grid is two columns, unchanged from the two copies this replaces.
 *
 * There is no `6`. Screen 10 has six tiles and briefly asked for all six in one
 * row, which at `md` (768px) gives each about 118px while the longest label is
 * "Live scoring enabled". The client ruled 2026-08-30 for a 2x3 layout instead,
 * so the console passes `columns={3}` and gets two rows of three at every
 * desktop width.
 *
 * The class is LOOKED UP, never interpolated. Tailwind scans source text for
 * complete class names, so a template literal such as `md:grid-cols-${columns}`
 * yields a class that is never generated and a row that silently stays at the
 * mobile two-column layout on every desktop.
 */
const COLUMN_CLASS = {
  3: "md:grid-cols-3",
  4: "md:grid-cols-4",
} as const;

interface StatsGridProps {
  stats: StatTile[];
  /** Defaults to 4 — the width both org-admin screens shipped with. */
  columns?: keyof typeof COLUMN_CLASS;
}

function StatsGrid({ stats, columns = 4 }: StatsGridProps) {
  return (
    <dl className={cn("grid grid-cols-2 gap-3", COLUMN_CLASS[columns])}>
      {stats.map((stat) => (
        <div
          key={stat.label}
          className="flex flex-col gap-1 rounded-2xl border border-[color:var(--border-primary)] bg-[color:var(--bg-primary)] px-4 py-3"
        >
          <dt className="text-xs uppercase tracking-wide text-[color:var(--text-tertiary)]">
            {stat.label}
          </dt>
          <dd className="text-2xl font-semibold text-[color:var(--text-primary)] [font-variant-numeric:tabular-nums]">
            {stat.value}
          </dd>
          {stat.foot ? (
            <dd className="text-xs text-[color:var(--text-tertiary)] [font-variant-numeric:tabular-nums]">
              {stat.foot}
            </dd>
          ) : null}
        </div>
      ))}
    </dl>
  );
}

export { StatsGrid };
